import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-15-september-2026";
const IMAGE_URL = "/assets/community/aaaeast%20ends%20dubdtttttt.png";
const OFFICIAL_TICKET_URL = "https://www.hiibiza.com/events/2026/eastenderz/2026-09-15";
const SPOTLIGHT_TICKET_URL = "https://www.ibiza-spotlight.com/shop/tickets/choose.php?event_id=425570";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const THEATRE_LINEUP = ["East End Dubs", "Marco Carola", "Gaskin", "Lola Palmer"];
const CLUB_ROOM_LINEUP = ["Paco Osuna", "Luuk van Dijk", "Chinonegro"];
const ALL_ARTISTS = [...THEATRE_LINEUP, ...CLUB_ROOM_LINEUP];

const KEYWORDS = [
  "East End Dubs Hï Ibiza 15 September 2026 tickets",
  "Eastenderz Hï Ibiza tickets",
  "East End Dubs presents Eastenderz Hï Ibiza",
  "Marco Carola Hï Ibiza Eastenderz",
  "Gaskin Hï Ibiza",
  "Lola Palmer Hï Ibiza",
  "Paco Osuna Now Here Hï Ibiza",
  "Luuk van Dijk Hï Ibiza",
  "Chinonegro Hï Ibiza",
  "Hï Ibiza Tuesday tickets",
  "Eastenderz Ibiza 2026",
  "HLT Ibiza concierge",
];

const FAQS = [
  {
    question: "When is East End Dubs presents Eastenderz at Hï Ibiza?",
    answer: "East End Dubs presents Eastenderz is at Hï Ibiza on Tuesday 15 September 2026, with doors listed from 23:30.",
  },
  {
    question: "Who is playing Eastenderz at Hï Ibiza on 15 September 2026?",
    answer: "The Theatre lists East End Dubs, Marco Carola, Gaskin and Lola Palmer. Club Room / Now Here lists Paco Osuna, Luuk van Dijk and Chinonegro.",
  },
  {
    question: "Where can I buy Eastenderz Hï Ibiza tickets?",
    answer: "Use the official Hï Ibiza event page or the Ibiza Spotlight ticket checkout linked in this HLT guide. Standard tickets were listed from 90 EUR when checked, but live availability can change.",
  },
  {
    question: "Where is Hï Ibiza?",
    answer: "Hï Ibiza is in Playa d'en Bossa, close to Ushuaïa Ibiza and the main resort strip, making it a practical base for a full Ibiza night out.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "East End Dubs presents Eastenderz at Hï Ibiza 15 September 2026: Tickets, Lineup and HLT Guide",
  description:
    "East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 15 September 2026 with Marco Carola, Gaskin, Lola Palmer, Paco Osuna, Luuk van Dijk and Chinonegro.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-09-15",
  dateModified: "2026-09-15",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "East End Dubs presents Eastenderz at Hï Ibiza",
  startDate: "2026-09-15T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Hï Ibiza",
    address: "Playa d'en Bossa, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: OFFICIAL_TICKET_URL,
    priceCurrency: "EUR",
    price: "90",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Eastenderz Tickets" }) {
  return (
    <a href={OFFICIAL_TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function SpotlightTicketCta({ children = "Ibiza Spotlight Tickets" }) {
  return (
    <a href={SPOTLIGHT_TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-secondary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaFifteenSeptemberArticle() {
  return (
    <>
      <Helmet>
        <title>Eastenderz Hï Ibiza 15 September 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 15 September 2026. Lineup, official tickets, Ibiza Spotlight ticket link and HLT Ibiza planning guide."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Eastenderz Hï Ibiza 15 September 2026 Tickets" />
        <meta property="og:description" content="Official tickets, lineup and HLT planning guide for East End Dubs presents Eastenderz at Hï Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eastenderz Hï Ibiza 15 September 2026 Tickets" />
        <meta name="twitter:description" content="East End Dubs, Marco Carola, Gaskin, Lola Palmer and Now Here at Hï Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              East End Dubs presents Eastenderz at Hï Ibiza: 15 September 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Eastenderz returns to Hï Ibiza tonight with East End Dubs joined by Marco Carola, Gaskin and Lola Palmer in The Theatre. Club Room / Now Here brings Paco Osuna, Luuk van Dijk and Chinonegro for a tougher second-room run.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official Hï tickets" />
              <SpotlightTicketCta />
              <ConciergeCta children="Plan Hï Ibiza with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hï Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 15 September 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>From 90 EUR when checked</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep tech, minimal house, rolling groove and Now Here club-room pressure</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_URL} alt="East End Dubs presents Eastenderz at Hï Ibiza 15 September 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                Eastenderz has become one of the cleanest Tuesday choices in Ibiza because it brings a true music-first identity into one of the island's biggest rooms. East End Dubs keeps the brand locked around deep tech, minimal house, dub, garage touches and rolling low-end pressure.
              </p>
              <p>
                Tonight is especially strong because Marco Carola joins The Theatre. That gives the night a proper Ibiza heavyweight next to East End Dubs, with Gaskin and Lola Palmer adding modern groove and selector depth around the main pairing.
              </p>
            </ArticleSection>

            <ArticleSection title="The Theatre: East End Dubs, Marco Carola, Gaskin and Lola Palmer">
              <p>
                East End Dubs is the anchor: stripped percussion, sharp basslines and long, hypnotic movement. In The Theatre, that sound has room to breathe while Hï's production gives it scale.
              </p>
              <p>
                Marco Carola brings decades of Music On-level Ibiza authority, so expect a more muscular, rolling tech-house edge when the room starts to peak. Gaskin adds UK club weight, while Lola Palmer brings a more detailed underground touch that fits the Eastenderz brief nicely.
              </p>
            </ArticleSection>

            <ArticleSection title="Club Room / Now Here">
              <p>
                Club Room / Now Here is Paco Osuna's lane, with Luuk van Dijk and Chinonegro also listed. This is the tougher side of the night: more direct percussion, more techno tension and a room that rewards people who want to move between spaces rather than stay planted in The Theatre.
              </p>
              <p>
                The best plan is to use both rooms properly. Start with Eastenderz for the groove, then step into Now Here when you want the pressure turned tighter and darker.
              </p>
            </ArticleSection>

            <ArticleSection title="Tickets and timing">
              <p>
                We are linking directly to the official Hï Ibiza event page for East End Dubs presents Eastenderz on Tuesday 15 September 2026. Standard tickets were listed from 90 EUR when checked, with availability moving by release tier.
              </p>
              <p>
                Ibiza Spotlight also has a direct checkout page for the same event. As always with Hï Ibiza, prices and availability can move quickly on the day, so lock the ticket before building the rest of the night.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on Hï Ibiza" />
                <SpotlightTicketCta children="Buy via Ibiza Spotlight" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Where to stay for Hï Ibiza">
              <p>
                Playa d'en Bossa is the easiest base if Eastenderz is the focus. You are close to Hï Ibiza, Ushuaïa, beach clubs and late-night food, so the night stays simple and transport stays manageable.
              </p>
              <p>
                Ibiza Town works if you want dinner, marina energy and a more polished pre-club route. San Antonio can work for sunset-led groups, but taxis or transfers need to be sorted before the island gets busy.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> East End Dubs presents Eastenderz</li>
                <li><strong className="text-white">Venue:</strong> Hï Ibiza, Playa d'en Bossa</li>
                <li><strong className="text-white">Date:</strong> Tuesday 15 September 2026</li>
                <li><strong className="text-white">Time:</strong> 23:30</li>
                <li><strong className="text-white">The Theatre:</strong> {THEATRE_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Club Room / Now Here:</strong> {CLUB_ROOM_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Tickets:</strong> From 90 EUR when checked</li>
                <li><strong className="text-white">Official ticket link:</strong> <a href={OFFICIAL_TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">Hï Ibiza event tickets</a></li>
                <li><strong className="text-white">Direct checkout:</strong> <a href={SPOTLIGHT_TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">Ibiza Spotlight tickets</a></li>
              </ul>
            </ArticleSection>

            <ArticleSection title="HLT verdict">
              <p>
                Eastenderz at Hï Ibiza tonight is a strong pick for anyone who wants big-room Ibiza without losing the underground thread. East End Dubs and Marco Carola give The Theatre serious weight, while Now Here gives the night a second, sharper gear.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy Eastenderz tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Lock the ticket, then build the night.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, transfer, table or group plan around Eastenderz at Hï Ibiza? HLT can shape the night around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Official Hï tickets" />
                <SpotlightTicketCta children="Ibiza Spotlight checkout" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 grid gap-3 text-sm leading-6 text-slate-300">
                <li><strong className="text-white">Arrive with a plan:</strong> Hï has multiple rooms and a late start.</li>
                <li><strong className="text-white">Book official:</strong> use live ticket links for current availability.</li>
                <li><strong className="text-white">Use both rooms:</strong> The Theatre gives Eastenderz groove, Club Room brings Now Here pressure.</li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
